/* eslint-disable @opendoor/no-html-elements */
/* eslint-disable jsdoc/require-jsdoc */
import React from 'react';
import styled from '@emotion/styled';

import { media } from '@opendoor/bricks/theme/legacy/foundations/mediaBreakpoints';
import { colors, heightsWithUnits } from '@opendoor/bricks/theme/eero';
import Footer from '@opendoor/bricks/deprecated/UNSAFE_layout/Footer';
import FooterFull from '@opendoor/bricks/deprecated/UNSAFE_layout/FooterFull';
import {
  ResponsiveHeader as Header,
  IProps as IHeaderProps,
} from 'sellDirect/components/legacy/layout/Header';

// used in bricks-storybook
export const HeadersTypes = [
  'BACK',
  'FULL',
  'HIDE_NAV',
  'MINIMAL',
  'BROKERAGE',
  'NONE',
  'BROKERAGE_MINIMAL',
] as const;
export const FootersTypes = ['FULL', 'MINIMAL', 'NONE'] as const;

export type HeaderType = typeof HeadersTypes[number];
export type FooterType = typeof FootersTypes[number];

export interface IProps {
  // Prefix of the name of the layout, which should just be the app name
  analyticsName: string;
  fixedHeader?: boolean;
  footerType?: FooterType;
  headerType?: HeaderType;
  mobileNavVisible?: IHeaderProps['mobileNavVisible'];
  hasRenderedNav?: IHeaderProps['hasRenderedNav'];
  renderAccountMenu?: IHeaderProps['renderAccountMenu'];
  renderExtra?: IHeaderProps['renderExtra'];
  renderLogo?: IHeaderProps['renderLogo'];
  onBackClick?: () => void;
  hideMobileNav?: () => void;
  showMobileNav?: () => void;
}

export const Layout: React.FC<IProps> = ({
  analyticsName,
  fixedHeader,
  footerType = 'MINIMAL',
  headerType = 'FULL',
  mobileNavVisible,
  hasRenderedNav,
  renderAccountMenu,
  renderExtra,
  renderLogo,
  onBackClick,
  hideMobileNav,
  showMobileNav,
  children,
}) => {
  const footer = getFooter(analyticsName, footerType);
  const header = getHeader(
    analyticsName,
    headerType,
    mobileNavVisible,
    hasRenderedNav,
    renderAccountMenu,
    renderLogo,
    renderExtra,
    onBackClick,
    hideMobileNav,
    showMobileNav,
  );

  const ODLayout = fixedHeader ? ODLayoutWithFixedHeader : BaseODLayout;

  return (
    <ODLayout>
      {fixedHeader ? <FixedHeader>{header}</FixedHeader> : header}
      <div>{children}</div>
      {footer}
    </ODLayout>
  );
};

const getFooter = (analyticsName: IProps['analyticsName'], footerType: FooterType) => {
  switch (footerType) {
    case 'MINIMAL':
      return <Footer analyticsName={`${analyticsName}-footer`} />;
    case 'FULL':
      return <FooterFull />;
    case 'NONE':
      return <div />;
    default:
      break;
  }
};

const getHeader = (
  analyticsName: IProps['analyticsName'],
  headerType: HeaderType,
  mobileNavVisible: IProps['mobileNavVisible'],
  hasRenderedNav: IProps['hasRenderedNav'],
  renderAccountMenu: IProps['renderAccountMenu'],
  renderLogo: IProps['renderLogo'],
  renderExtra: IProps['renderExtra'],
  onBackClick: IProps['onBackClick'],
  hideMobileNav: IProps['hideMobileNav'],
  showMobileNav: IProps['showMobileNav'],
) => {
  switch (headerType) {
    case 'FULL':
      return (
        <Header
          analyticsName={`${analyticsName}-header`}
          mobileNavVisible={mobileNavVisible}
          hasRenderedNav={hasRenderedNav}
          renderAccountMenu={renderAccountMenu}
          renderLogo={renderLogo}
          onBackClick={onBackClick}
          hideMobileNav={hideMobileNav}
          showMobileNav={showMobileNav}
        />
      );
    case 'HIDE_NAV':
      return (
        <Header
          analyticsName={`${analyticsName}-header`}
          hideNav
          mobileNavVisible={mobileNavVisible}
          hasRenderedNav={hasRenderedNav}
          renderAccountMenu={renderAccountMenu}
          renderLogo={renderLogo}
          renderExtra={renderExtra}
          onBackClick={onBackClick}
          hideMobileNav={hideMobileNav}
          showMobileNav={showMobileNav}
        />
      );
    case 'MINIMAL':
      return (
        <Header
          analyticsName={`${analyticsName}-header`}
          hideAccount
          hideNav
          mobileNavVisible={mobileNavVisible}
          hasRenderedNav={hasRenderedNav}
          onBackClick={onBackClick}
          hideMobileNav={hideMobileNav}
          showMobileNav={showMobileNav}
        />
      );
    case 'BROKERAGE':
      return (
        <Header
          analyticsName={`${analyticsName}-header`}
          hideNav
          mobileNavVisible={mobileNavVisible}
          hasRenderedNav={hasRenderedNav}
          showBrokerageLogo
          onBackClick={onBackClick}
          hideMobileNav={hideMobileNav}
          showMobileNav={showMobileNav}
        />
      );
    case 'BROKERAGE_MINIMAL':
      return (
        <Header
          analyticsName={`${analyticsName}-header`}
          hideAccount
          hideNav
          mobileNavVisible={mobileNavVisible}
          hasRenderedNav={hasRenderedNav}
          hideBorder
          showBrokerageLogo
          onBackClick={onBackClick}
          hideMobileNav={hideMobileNav}
          showMobileNav={showMobileNav}
        />
      );
    case 'NONE':
      return <div />;
    default:
      break;
  }
};

Layout.displayName = 'Layout';

const BaseODLayout = styled.div`
  min-height: 100vh;
  background-color: ${colors.neutrals0};
  background-image: none;

  ${media.smallerThan.SM} {
    background-color: ${colors.neutrals0};
    background-image: none;
  }
`;

const ODLayoutWithFixedHeader = styled(BaseODLayout)`
  padding-top: ${heightsWithUnits.header};

  ${media.smallerThan.XS} {
    padding-top: 52px;
  }
`;

export const FixedHeaderZIndex = 100;

const FixedHeader = styled.div`
  background-color: ${colors.neutrals0};
  border-bottom: 1px solid ${colors.neutrals30};
  box-sizing: border-box;
  left: 0;
  position: fixed;
  top: 0;
  right: 0;
  z-index: ${FixedHeaderZIndex};
`;
